# Clef note finder — browser wiring (/:lang/apps/clef-notes and /embed).
#
# Draws the staff (Bravura glyphs, SMuFL metrics), finds the line or space
# under a tap, names the note, lights and plays the piano key, and runs the
# same lookup backwards from a key. The maths is clef-notes-core.coffee, which
# jest tests; this file only draws and listens. Rules: docs/free-tools.md.

do ->
    SVG_NS = 'http://www.w3.org/2000/svg'

    # ---------------------------------------------------------------- helpers
    make = (tag, className, text) ->
        node = document.createElement(tag)
        node.className = className if className
        node.textContent = text if text?
        node

    svgNode = (tag, attrs) ->
        node = document.createElementNS(SVG_NS, tag)
        node.setAttribute(k, v) for own k, v of (attrs or {})
        node

    emptyNode = (node) ->
        node.removeChild(node.firstChild) while node and node.firstChild
        node

    fill = (template, params) ->
        out = String(template or '')
        for own k, v of (params or {})
            out = out.split('{{' + k + '}}').join(v)
        out

    # localStorage is often blocked in a third-party frame and in private
    # windows. A preference that does not persist is fine; a throw is not.
    store = (key, value) ->
        try
            if value?
                window.localStorage.setItem(key, String(value))
                value
            else
                window.localStorage.getItem(key)
        catch e
            null

    boot = ->
        cfg = window.clefNotesConfig
        C = window.SongtiveClefNotes
        root = document.getElementById('cn-widget')
        return unless cfg and C and root
        T = C.theory
        S = cfg.strings or {}

        # ------------------------------------------------------------- state
        state =
            mode: C.normalizeMode(cfg.mode)
            acc: 0
            names: store('clefnotes:names') is '1'
            notation: if store("clefnotes:notation:#{cfg.lang}") is 'latin' then 'latin' else 'local'
            note: null
        spec = -> if state.notation is 'latin' then (cfg.latin or T.LATIN) else (cfg.notation or T.LATIN)
        noteName = (note, withOctave = false) -> C.display(note, spec(), withOctave)

        # --------------------------------------------------------- analytics
        # One event name for the whole tool: the GA4 property is near its
        # event-name limit, so `action` carries the detail. The embed adds
        # embed=1 and embed_host to every event.
        lastSelectAt = 0
        track = (action, params = {}) ->
            try
                merged = { action: action }
                merged[k] = v for own k, v of (cfg.eventParams or {})
                merged[k] = v for own k, v of params
                if typeof window.gtag is 'function'
                    window.gtag('event', 'clef_notes', merged)
                else if window.app and typeof window.app.gaEvent is 'function'
                    window.app.gaEvent('clef_notes', null, action + ':' + (params.note or params.mode or ''))
            catch e
            undefined

        # ------------------------------------------------------------- audio
        # Nothing sounds until a tap: every call below comes from a gesture.
        # ToolsAudio is the online piano's engine (MIDI.js + FluidR3); the games
        # core synth covers when it is missing.
        # Where the page did not load MIDI.js up front (the glossary), it comes
        # in on the first tap; that first note uses the synth, the rest the piano.
        midiAsked = false
        loadMidi = ->
            return if midiAsked or window.MIDI?.loadPlugin or not cfg.midiScripts?.length
            midiAsked = true
            for url in cfg.midiScripts
                node = document.createElement('script')
                node.src = url
                node.async = false
                document.head.appendChild(node)
            undefined

        play = (midi) ->
            return unless midi?
            loadMidi()
            try
                if window.toolsAudio
                    window.toolsAudio.ctx?.resume?() if window.toolsAudio.ctx?.state is 'suspended'
                    window.toolsAudio.playNote(midi, 0, 1.2)
                else
                    window.ChordIQGames?.audio?.playNote(midi, 0, 1.2)
            catch e
            undefined

        # --------------------------------------------------------------- font
        fontReady = false
        FONT_PROBE = T.GLYPHS.gClef + T.GLYPHS.noteheadWhole + T.GLYPHS.brace
        loadFont = (done) ->
            finished = false
            settle = (ok) ->
                return if finished
                finished = true
                fontReady = ok
                done()
            unless document.fonts?.load
                return settle(false)
            try
                document.fonts.load("1em #{T.SMUFL.family}", FONT_PROBE).then(
                    (faces) -> settle(!!(faces and faces.length))
                    -> settle(false))
                window.setTimeout((-> settle(false)), 4000)
            catch e
                settle(false)
            undefined

        # ----------------------------------------------------------- geometry
        # Everything in staff spaces: GAP units between two lines, one step
        # (line to space) is half of that. The viewBox is 200 wide; the CSS
        # aspect-ratio on .cn-staff reserves the same shape before this runs.
        # The same layout the server uses for the static staff
        # (app/seo/clef-notes-view.coffee), so nothing moves on the upgrade.
        L = C.LAYOUT
        GAP = L.GAP
        W = L.W
        STAFF_LEFT = L.STAFF_LEFT
        STAFF_RIGHT = L.STAFF_RIGHT
        NOTE_X = L.NOTE_X
        MARK_X = L.MARK_X
        LABEL_X = L.LABEL_X

        geometry = null
        layout = -> geometry = C.layout(state.mode)

        stepY = (clef, step) -> geometry.stepY(clef, step)

        # --------------------------------------------------------------- staff
        host = document.getElementById('cn-staff')
        svg = null
        layers = {}

        glyph = (name, x, y, className) ->
            node = svgNode('text',
                x: x, y: y
                'font-family': T.SMUFL.family
                'font-size': GAP * T.SMUFL.spacesPerEm
                'class': 'cn-glyph' + (if className then ' ' + className else ''))
            node.textContent = T.GLYPHS[name] or ''
            node

        line = (x1, x2, y, thickness, className) ->
            svgNode('line', { x1: x1, x2: x2, y1: y, y2: y, 'stroke-width': thickness * GAP, 'class': className })

        # Hand-drawn clefs and brace for a font that failed to load, so the
        # staff never shows a raw private-use character. Same strokes as the
        # games core draws in the same situation.
        fallbackTreble = (x, gy) ->
            g = GAP * 0.88
            d = [
                "M #{x - 0.30 * g} #{gy + 2.50 * g}"
                "c #{0.70 * g} #{0.42 * g} #{1.32 * g} #{-0.22 * g} #{1.20 * g} #{-0.96 * g}"
                "c #{-0.10 * g} #{-0.62 * g} #{-0.52 * g} #{-1.30 * g} #{-0.90 * g} #{-2.10 * g}"
                "c #{-0.62 * g} #{-1.30 * g} #{-1.24 * g} #{-2.30 * g} #{-1.24 * g} #{-3.30 * g}"
                "c 0 #{-1.15 * g} #{0.66 * g} #{-1.95 * g} #{1.16 * g} #{-1.95 * g}"
                "c #{0.52 * g} 0 #{0.86 * g} #{0.60 * g} #{0.86 * g} #{1.40 * g}"
                "c 0 #{1.30 * g} #{-0.92 * g} #{2.30 * g} #{-2.06 * g} #{3.02 * g}"
                "c #{-1.16 * g} #{0.74 * g} #{-2.00 * g} #{1.50 * g} #{-2.00 * g} #{2.52 * g}"
                "c 0 #{1.08 * g} #{0.88 * g} #{1.86 * g} #{1.90 * g} #{1.86 * g}"
                "c #{0.94 * g} 0 #{1.62 * g} #{-0.66 * g} #{1.62 * g} #{-1.48 * g}"
                "c 0 #{-0.70 * g} #{-0.50 * g} #{-1.22 * g} #{-1.16 * g} #{-1.22 * g}"
                "c #{-0.56 * g} 0 #{-0.96 * g} #{0.38 * g} #{-0.96 * g} #{0.84 * g}"
            ].join(' ')
            [svgNode('path', { d: d, 'class': 'cn-fallback', 'stroke-width': 1.6 })]

        fallbackBass = (x, fy) ->
            g = GAP * 0.72
            d = [
                "M #{x + 1.05 * g} #{fy - 0.55 * g}"
                "c 0 #{-0.70 * g} #{-0.62 * g} #{-1.18 * g} #{-1.40 * g} #{-1.18 * g}"
                "c #{-0.98 * g} 0 #{-1.75 * g} #{0.74 * g} #{-1.75 * g} #{1.80 * g}"
                "c 0 #{1.55 * g} #{1.10 * g} #{2.65 * g} #{2.55 * g} #{3.40 * g}"
            ].join(' ')
            dotX = x + 1.55 * g
            [
                svgNode('path', { d: d, 'class': 'cn-fallback', 'stroke-width': 1.6 })
                svgNode('circle', { cx: dotX, cy: fy - 0.5 * GAP, r: 0.15 * GAP, 'class': 'cn-fallback-dot' })
                svgNode('circle', { cx: dotX, cy: fy + 0.5 * GAP, r: 0.15 * GAP, 'class': 'cn-fallback-dot' })
            ]

        # The C clef as two heavy bars and two curves meeting on the centre line.
        fallbackAlto = (x, staff) ->
            mid = (staff.top + staff.bottom) / 2
            curve = (dir) ->
                end = if dir < 0 then staff.top else staff.bottom
                "M #{x + 9} #{mid} C #{x + 16} #{mid + dir * 2} #{x + 22} #{mid + dir * 12} #{x + 13} #{end}"
            [
                svgNode('rect', { x: x, y: staff.top, width: 3.5, height: staff.bottom - staff.top, 'class': 'cn-fallback-dot' })
                svgNode('rect', { x: x + 5.5, y: staff.top, width: 1.2, height: staff.bottom - staff.top, 'class': 'cn-fallback-dot' })
                svgNode('path', { d: curve(-1), 'class': 'cn-fallback', 'stroke-width': 2 })
                svgNode('path', { d: curve(1), 'class': 'cn-fallback', 'stroke-width': 2 })
            ]

        fallbackBrace = (x, top, bottom) ->
            mid = (top + bottom) / 2
            d = "M #{x + 5} #{top} C #{x} #{top + 10} #{x + 5} #{mid - 12} #{x} #{mid} C #{x + 5} #{mid + 12} #{x} #{bottom - 10} #{x + 5} #{bottom}"
            svgNode('path', { d: d, 'class': 'cn-fallback', 'stroke-width': 1.6 })

        drawFrame = ->
            layout()
            emptyNode(host)
            svg = svgNode('svg',
                viewBox: "0 0 #{W} #{geometry.height}"
                'role': 'img'
                'aria-label': S.staff or ''
                'tabindex': '0'
                'focusable': 'true')
            for name in ['guides', 'lines', 'clefs', 'labels', 'mark', 'ghost', 'note']
                layers[name] = svgNode('g', { 'class': 'cn-layer-' + name })
                svg.appendChild(layers[name])
            eng = T.SMUFL.engraving
            ledgerHalf = (T.SMUFL.box.noteheadWhole.w / 2 + eng.legerLineExtension) * GAP

            for clef in geometry.clefs
                staff = geometry.staves[clef]
                for i in [0..4]
                    layers.lines.appendChild line(STAFF_LEFT, STAFF_RIGHT, staff.top + i * GAP, eng.staffLineThickness, 'cn-line')
                # Faint dashes where the ledger lines would go: they tell a
                # visitor the space above and below the staff is tappable.
                for step in [-2, -4, -6, 10, 12, 14]
                    layers.guides.appendChild line(NOTE_X - ledgerHalf, NOTE_X + ledgerHalf, stepY(clef, step), eng.legerLineThickness, 'cn-guide')
                # The clef's baseline sits on the line it names: the G clef on
                # the second line, the F clef on the fourth.
                anchor = T.SMUFL.clefs[clef]
                cy = stepY(clef, anchor.step)
                if fontReady
                    layers.clefs.appendChild glyph(anchor.glyph, L.CLEF_X, cy, 'cn-clef')
                else
                    shapes =
                        if clef is 'bass' then fallbackBass(STAFF_LEFT + 10, cy)
                        else if clef is 'alto' then fallbackAlto(STAFF_LEFT + 4, geometry.staves.alto)
                        else fallbackTreble(STAFF_LEFT + 16, cy)
                    layers.clefs.appendChild(s) for s in shapes

            # Left edge of the system; a brace and a line join the grand staff.
            first = geometry.staves[geometry.clefs[0]]
            last = geometry.staves[geometry.clefs[geometry.clefs.length - 1]]
            layers.lines.appendChild svgNode('line',
                x1: STAFF_LEFT, x2: STAFF_LEFT, y1: first.top, y2: last.bottom
                'stroke-width': 0.16 * GAP, 'class': 'cn-line')
            if geometry.clefs.length > 1
                if fontReady
                    # The brace glyph is one staff tall; stretch it to the system.
                    span = last.bottom - first.top
                    brace = glyph('brace', 0, 0, 'cn-brace')
                    sy = span / (T.SMUFL.box.brace.top * GAP)
                    brace.setAttribute('transform', "translate(1 #{last.bottom}) scale(2.6 #{sy})")
                    layers.lines.appendChild(brace)
                else
                    layers.lines.appendChild fallbackBrace(2, first.top, last.bottom)

            host.appendChild(svg)
            bindStaff()
            drawLabels()
            drawMark()
            undefined

        # Middle C, the reference every clef is read from: always marked, in its
        # own colour and column, whatever the visitor taps. On the alto staff it
        # sits on the clef's centre line — the C clef names that line.
        drawMark = ->
            return unless svg
            emptyNode(layers.mark)
            mark = C.middleMark(state.mode)
            y = stepY(mark.clef, mark.step)
            eng = T.SMUFL.engraving
            half = (T.SMUFL.box.noteheadWhole.w / 2 + eng.legerLineExtension) * GAP
            for ledger in T.ledgerSteps(mark.step)
                layers.mark.appendChild line(MARK_X - half, MARK_X + half, stepY(mark.clef, ledger), eng.legerLineThickness, 'cn-mark-ledger')
            head(MARK_X, y, 'cn-mark', layers.mark)
            staff = geometry.staves[mark.clef]
            ly =
                if mark.step < 0 then y + 1.3 * GAP
                else if mark.step > 8 then y - 0.9 * GAP
                else staff.bottom + 1.3 * GAP
            text = markLabel()
            label = svgNode('text',
                x: MARK_X, y: ly, 'font-size': C.markLabelSize(text), 'text-anchor': 'middle', 'class': 'cn-mark-label')
            label.textContent = text
            layers.mark.appendChild(label)
            undefined

        markLabel = ->
            middle = fill(S.middle, { note: T.spell(0, 0, spec()) })
            middle = middle.charAt(0).toUpperCase() + middle.slice(1)
            c4 = C.stepToNote('treble', -2)
            "#{middle} (#{noteName(c4, true)})"

        # The cheat-sheet view: every line and space named, lines in one
        # column and spaces in the next so the labels never collide.
        drawLabels = ->
            return unless svg
            emptyNode(layers.labels)
            return unless state.names
            for clef in geometry.clefs
                for step in C.steps()
                    n = C.stepToNote(clef, step)
                    isOn = state.note and state.note.clef is clef and state.note.step is step
                    text = svgNode('text',
                        x: (if C.isLine(step) then LABEL_X.line else LABEL_X.space)
                        y: stepY(clef, step) + 2.2
                        'font-size': 6.4
                        'text-anchor': 'middle'
                        'class': 'cn-label' + (if isOn then ' is-on' else ''))
                    text.textContent = noteName(n, true)
                    layers.labels.appendChild(text)
            undefined

        head = (x, y, className, layer) ->
            if fontReady
                box = T.SMUFL.box.noteheadWhole
                layer.appendChild glyph('noteheadWhole', x - box.w / 2 * GAP, y, className)
            else
                layer.appendChild svgNode('ellipse',
                    cx: x, cy: y, rx: 0.8 * GAP, ry: 0.5 * GAP, 'class': className)
            undefined

        accidental = (alter, x, y, className, layer) ->
            name = T.accidentalGlyph(alter)
            return unless name
            headLeft = x - T.SMUFL.box.noteheadWhole.w / 2 * GAP
            if fontReady
                box = T.SMUFL.box[name]
                layer.appendChild glyph(name, headLeft - 0.3 * GAP - box.w * GAP, y, className)
            else
                mark = svgNode('text',
                    x: headLeft - 1.3 * GAP, y: y + 0.45 * GAP, 'font-size': 1.9 * GAP, 'class': className)
                mark.textContent = if alter > 0 then '♯' else '♭'
                layer.appendChild(mark)
            undefined

        drawNote = ->
            emptyNode(layers.note)
            note = state.note
            return unless note and svg
            y = stepY(note.clef, note.step)
            eng = T.SMUFL.engraving
            half = (T.SMUFL.box.noteheadWhole.w / 2 + eng.legerLineExtension) * GAP
            for ledger in T.ledgerSteps(note.step)
                layers.note.appendChild line(NOTE_X - half, NOTE_X + half, stepY(note.clef, ledger), eng.legerLineThickness, 'cn-line cn-note-ledger')
            head(NOTE_X, y, 'cn-note', layers.note)
            accidental(note.alter, NOTE_X, y, 'cn-note', layers.note)
            label = svgNode('text',
                x: NOTE_X + 13, y: y + 3, 'font-size': 8.5, 'class': 'cn-note-label')
            label.textContent = noteName(note, true)
            layers.note.appendChild(label)
            drawLabels() if state.names
            undefined

        drawGhost = (target) ->
            return unless svg
            emptyNode(layers.ghost)
            return unless target
            return if state.note and target.clef is state.note.clef and target.step is state.note.step
            head(NOTE_X, stepY(target.clef, target.step), 'cn-ghost', layers.ghost)
            undefined

        # The line or space nearest to a point, across every staff drawn.
        nearest = (evt) ->
            return null unless svg
            try
                point = svg.createSVGPoint()
                point.x = evt.clientX
                point.y = evt.clientY
                local = point.matrixTransform(svg.getScreenCTM().inverse())
            catch e
                return null
            best = null
            for clef in geometry.clefs
                for step in C.steps()
                    dist = Math.abs(stepY(clef, step) - local.y)
                    best = { clef: clef, step: step, dist: dist } if not best or dist < best.dist
            best

        bindStaff = ->
            svg.addEventListener 'click', (e) ->
                target = nearest(e)
                return unless target
                select(C.stepToNote(target.clef, target.step, state.acc), 'staff')
            # A hover preview, for a mouse only: on a touch screen there is no
            # hover, and a finger dragging over the staff is a scroll.
            svg.addEventListener 'pointermove', (e) ->
                return unless e.pointerType is 'mouse'
                drawGhost(nearest(e))
            svg.addEventListener 'pointerleave', -> drawGhost(null)
            svg.addEventListener 'keydown', (e) ->
                switch e.key
                    when 'ArrowUp' then nudge(1)
                    when 'ArrowDown' then nudge(-1)
                    when 'Enter', ' ' then play(state.note?.midi)
                    else return
                e.preventDefault()
            undefined

        # --------------------------------------------------------- keyboard
        keysHost = document.getElementById('cn-keys')
        keysWrap = document.getElementById('cn-keys-wrap')
        keyByMidi = {}
        WHITE = [0, 2, 4, 5, 7, 9, 11]
        isWhite = (m) -> (m % 12) in WHITE

        keyNote = (midi) -> C.spellMidi(midi, state.acc is -1)

        drawKeys = ->
            emptyNode(keysHost)
            keyByMidi = {}
            range = C.keyboardRange(state.mode)
            whites = (m for m in [range.from..range.to] when isWhite(m))
            # 26 px a white key at least: under that a thumb hits two keys.
            keysHost.style.minWidth = (whites.length * 26) + 'px'
            row = make('div', 'cn-keys__whites')
            for m in whites
                key = make('button', 'cn-key cn-key--white')
                key.type = 'button'
                key.setAttribute('data-midi', m)
                key.appendChild(make('span', 'cn-key__label'))
                row.appendChild(key)
                keyByMidi[m] = key
            keysHost.appendChild(row)
            width = 100 / whites.length
            for m in [range.from..range.to]
                continue if isWhite(m)
                below = (w for w in whites when w < m).length
                key = make('button', 'cn-key cn-key--black')
                key.type = 'button'
                key.setAttribute('data-midi', m)
                key.style.left = (below * width - width * 0.3) + '%'
                key.style.width = (width * 0.6) + '%'
                key.appendChild(make('span', 'cn-key__label'))
                keysHost.appendChild(key)
                keyByMidi[m] = key
            labelKeys()
            undefined

        labelKeys = ->
            current = state.note?.midi
            for own m, key of keyByMidi
                midi = parseInt(m, 10)
                n = keyNote(midi)
                full = noteName(n, true)
                key.setAttribute('aria-label', full)
                key.classList.toggle('is-on', midi is current)
                # Middle C keeps its reference colour while another key is lit.
                key.classList.toggle('is-ref', midi is 60 and midi isnt current)
                text =
                    if midi is current then full
                    else if not isWhite(midi) then ''
                    else if n.letter is 'C' then full
                    else if state.names then noteName(n, false)
                    else ''
                key.firstChild.textContent = text
            undefined

        # Bring the lit key into view inside the scroller only — never scroll
        # the page, which scrollIntoView would do on a phone.
        revealKey = (midi) ->
            key = keyByMidi[midi]
            return unless key and keysWrap
            left = key.offsetLeft
            right = left + key.offsetWidth
            view = keysWrap.scrollLeft
            if left < view + 20 or right > view + keysWrap.clientWidth - 20
                keysWrap.scrollLeft = Math.max(0, left - keysWrap.clientWidth / 2 + key.offsetWidth / 2)
            undefined

        keysHost.addEventListener 'click', (e) ->
            target = e.target
            target = target.parentNode while target and target isnt keysHost and not target.getAttribute?('data-midi')
            return unless target and target isnt keysHost
            midi = parseInt(target.getAttribute('data-midi'), 10)
            note = C.placeMidi(state.mode, midi, state.acc is -1)
            select(note, 'key') if note

        # ----------------------------------------------------------- answer
        els =
            name: document.getElementById('cn-name')
            oct: document.getElementById('cn-oct')
            where: document.getElementById('cn-where')
            octave: document.getElementById('cn-octave')
            middle: document.getElementById('cn-middle')
            also: document.getElementById('cn-also')

        whereText = (note) ->
            fill(S.where, { clef: S['clef_' + note.clef], position: S['pos_' + C.positionKey(note.step)] })

        drawAnswer = ->
            note = state.note
            return unless note
            els.name.textContent = noteName(note, false)
            els.oct.textContent = note.octave
            els.where.textContent = whereText(note)
            els.octave.textContent = fill(S.octave, { n: note.octave }) + ' · ' + fill(S.spn, { name: noteName(note, true) })
            isMiddle = note.diatonic is C.MIDDLE_C and not note.alter
            els.middle.hidden = not isMiddle
            els.middle.textContent = markLabel()
            alt = C.alternate(state.mode, note)
            els.also.hidden = not alt
            els.also.textContent = if alt then fill(S.also, { where: whereText(alt) }) else ''
            undefined

        # ----------------------------------------------------------- select
        select = (note, source, sound = true) ->
            return unless note
            state.note = note
            drawNote()
            drawGhost(null)
            labelKeys()
            revealKey(note.midi)
            drawAnswer()
            if sound
                play(note.midi)
                now = Date.now()
                # One event per burst of taps, not per tap.
                if now - lastSelectAt > 1500
                    track('select', { source: source, clef: note.clef, note: C.spn(note), mode: state.mode })
                lastSelectAt = now
            undefined

        # One step up or down. Off the end of a staff on the grand staff, the
        # note carries on in the other staff at the next pitch.
        nudge = (delta) ->
            note = state.note
            return unless note
            step = note.step + delta
            if C.inRange(step)
                return select(C.stepToNote(note.clef, step, note.alter), 'nudge')
            return unless state.mode is 'grand'
            spelled = { diatonic: note.diatonic + delta, alter: note.alter }
            other = if note.clef is 'treble' then 'bass' else 'treble'
            otherStep = T.staffStep(other, spelled.diatonic)
            select(C.stepToNote(other, otherStep, note.alter), 'nudge') if C.inRange(otherStep)

        document.getElementById('cn-up')?.addEventListener 'click', -> nudge(1)
        document.getElementById('cn-down')?.addEventListener 'click', -> nudge(-1)
        document.getElementById('cn-play')?.addEventListener 'click', -> play(state.note?.midi)

        # ---------------------------------------------------------- controls
        pressed = (group, attr, value) ->
            return unless group
            for btn in group.querySelectorAll('[' + attr + ']')
                btn.setAttribute('aria-pressed', String(btn.getAttribute(attr) is String(value)))
            undefined

        setMode = (mode, fromUser = true) ->
            state.mode = C.normalizeMode(mode)
            root.setAttribute('data-mode', state.mode)
            host.setAttribute('data-mode', state.mode)
            pressed(document.getElementById('cn-mode'), 'data-mode', state.mode)
            previous = state.note
            drawFrame()
            drawKeys()
            # Keep the pitch where the new staff can show it; otherwise go back
            # to middle C, which every mode shows.
            placed = if previous then C.placeNote(state.mode, previous) else null
            select(placed or C.placeMidi(state.mode, 60), 'mode', false)
            if fromUser
                track('mode', { mode: state.mode })
                # The view is in the address, so a shared link opens it again.
                try
                    url = new URL(window.location.href)
                    if state.mode is 'grand' then url.searchParams.delete('clef') else url.searchParams.set('clef', state.mode)
                    window.history.replaceState(null, '', url.toString())
                catch e
            undefined

        modeGroup = document.getElementById('cn-mode')
        modeGroup?.addEventListener 'click', (e) ->
            btn = e.target.closest?('[data-mode]')
            setMode(btn.getAttribute('data-mode')) if btn

        accGroup = document.getElementById('cn-acc')
        accGroup?.addEventListener 'click', (e) ->
            btn = e.target.closest?('[data-acc]')
            return unless btn
            state.acc = parseInt(btn.getAttribute('data-acc'), 10) or 0
            pressed(accGroup, 'data-acc', state.acc)
            # The accidental applies to the note on the staff straight away.
            if state.note
                select(C.stepToNote(state.note.clef, state.note.step, state.acc), 'accidental')
            else
                labelKeys()

        names = document.getElementById('cn-names')
        if names
            names.checked = state.names
            names.addEventListener 'change', ->
                state.names = !!names.checked
                store('clefnotes:names', if state.names then '1' else '0')
                drawLabels()
                labelKeys()
                track('names', { on: if state.names then 1 else 0 })

        notationSelect = document.getElementById('cn-notation')
        if notationSelect
            notationSelect.value = state.notation
            notationSelect.addEventListener 'change', ->
                state.notation = if notationSelect.value is 'latin' then 'latin' else 'local'
                store("clefnotes:notation:#{cfg.lang}", state.notation)
                drawMark()
                drawNote()
                drawLabels()
                labelKeys()
                drawAnswer()

        # The embed snippet on the full page: clipboard, select as fallback.
        copyBtn = document.getElementById('cn-embed-copy')
        code = document.getElementById('cn-embed-code')
        if copyBtn and code
            copyBtn.addEventListener 'click', ->
                done = ->
                    note = document.getElementById('cn-embed-copied')
                    note.hidden = false if note
                    track('embed_copy')
                try
                    if navigator.clipboard?.writeText
                        navigator.clipboard.writeText(code.value).then(done, ->
                            code.select()
                            document.execCommand?('copy')
                            done())
                    else
                        code.select()
                        document.execCommand?('copy')
                        done()
                catch e
                    code.select()

        # --------------------------------------------------------------- go
        # On a glossary page the live widget is rendered hidden beside a static
        # staff; swap them now that the script is here.
        if root.hidden
            root.hidden = false
            document.getElementById('cn-static')?.setAttribute('hidden', 'hidden')
        pressed(document.getElementById('cn-mode'), 'data-mode', state.mode)
        drawFrame()
        drawKeys()
        select(C.placeMidi(state.mode, 60), 'boot', false)
        # Repaint in Bravura once the font is in; until then the hand-drawn
        # clefs stand in, so nothing ever shows a raw glyph.
        loadFont ->
            return unless fontReady
            drawFrame()
            drawNote()
        undefined

    if document.readyState is 'loading'
        document.addEventListener('DOMContentLoaded', boot)
    else
        boot()
